import React, { useState, createContext, useCallback } from 'react';
import { API } from 'aws-amplify';
import { useAuth } from './Auth';
import { DevicesResponse } from '../models/SimScheduleInterface';
import { useApiError } from './ApiErrorProvider';
import { error } from '../constants/bindings';

const SimScheduleContext = createContext({
  equipmentOptions: [],
  getEquipmentsData: () => { },
  getDevicesData: (equipmentId: string) =>
    Promise.resolve({ deviceList: [] } as DevicesResponse),
  getDeviceReportUrl: (deviceId: string) => Promise.resolve({ reportUrl: '', message: '' }),
});

export const SimScheduleProvider = (props) => {
  const { user } = useAuth();
  const { setErrorCode } = useApiError();
  const [equipmentOptions, setEquipmentOptions] = useState([]);

  const getEquipmentsData = async () => {
    if (!user || equipmentOptions.length) {
      return;
    }
    try {
      const equipmentData = await API.get('crewlife', `/equipments`, {});
      let updated = equipmentData.results.map((e) => e.equipmentcode);
      setEquipmentOptions(updated);
    } catch (e: any) {
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
      return [];
    }
  };

  const getDevicesData = useCallback(
    async (equipmentId: string): Promise<DevicesResponse> => {
      try {
        const requestObject = {
          body: {
            fleet: equipmentId
          }
        };

        // Make API call
        const response = await API.post(
          'crewlife',
          '/sim/retrieve',
          requestObject
        );

        // Map the response to DevicesResponse format
        const deviceList: DevicesResponse['deviceList'] = [];

        // Map IPT devices (type: 'PT')
        if (response.IPT && Array.isArray(response.IPT)) {
          response.IPT.forEach(
            (device: { deviceId: string | number; deviceName: string }) => {
              deviceList.push({
                id: device.deviceId.toString(),
                name: device.deviceName,
                type: 'PT'
              });
            }
          );
        }

        // Map Simulator devices (type: 'SIM')
        if (response.Simulator && Array.isArray(response.Simulator)) {
          response.Simulator.forEach(
            (device: { deviceId: string | number; deviceName: string }) => {
              deviceList.push({
                id: device.deviceId.toString(),
                name: device.deviceName,
                type: 'SIM'
              });
            }
          );
        }

        // Map VSIM devices (type: 'VSIM')
        if (response.VSIM && Array.isArray(response.VSIM)) {
          response.VSIM.forEach(
            (device: { deviceId: string | number; deviceName: string }) => {
              deviceList.push({
                id: device.deviceId.toString(),
                name: device.deviceName,
                type: 'VSIM'
              });
            }
          );
        }

        return {
          deviceList: deviceList
        };
      } catch (e: any) {
        console.error('Error fetching devices data:', e);
        if (e.response?.status) {
          setErrorCode(e.response?.status);
        } else {
          setErrorCode(error.DEFAULT);
        }

        return {
          deviceList: []
        };
      }
    },
    [setErrorCode]
  );

  const getDeviceReportUrl = useCallback(
    async (deviceId: string): Promise<{ reportUrl: string, message: string }> => {
      try {
        const requestObject = {
          body: {
            deviceId: parseInt(deviceId)
            // deviceId: 22
          }
        };

        // Make API call to get report URL
        const response = await API.post(
          'crewlife',
          '/sim/report',
          requestObject
        );

        console.log('Report response:', response);

        return {
          reportUrl: response.scheduleFileUrl || '',
          message: response.message || ''
        };
      } catch (e: any) {
        console.error('Error fetching report URL:', e);

        if (e.response?.status === 400) {
          console.error('Missing device ID');
        } else if (e.response?.status === 404) {
          console.error('Report not available for this device');
        } else {
          if (e.response?.status) {
            setErrorCode(e.response?.status);
          } else {
            setErrorCode(error.DEFAULT);
            return {
              reportUrl: '',
              message: 'Error fetching report URL'
            };
          }
        }

        return { reportUrl: '', message: 'Error fetching report URL' };
      }
    },
    [setErrorCode]
  );

  return (
    <SimScheduleContext.Provider
      value={{
        equipmentOptions,
        getEquipmentsData,
        getDevicesData,
        getDeviceReportUrl
      }}
    >
      {props.children}
    </SimScheduleContext.Provider>
  );
};

export const useSimScheduleProvider = () => {
  return React.useContext(SimScheduleContext);
};
